import { useWhitelabel } from "hooks";
import usePayment from "hooks/usePayment";
import React, { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { EnumFormaPagamento } from "services";
import {
  EnumOriginPayment,
  EnumTypeDebit,
  MappedDebitsToGetInstallments,
} from "services/CreditCard";
import { formatCurrency } from "utils";
import { ContainerFlexCol } from "../styles";
import CardMethodPayment from "./CardMethodsPayment";

interface OptionsPaymentsProps {
  setPaymentMethod: (method: EnumFormaPagamento | null) => void;
}

type KeyOptionsCharge = Exclude<
  keyof typeof EnumFormaPagamento,
  "MULTIPLE_CREDIT"
>;

const OptionsPayments = ({ setPaymentMethod }: OptionsPaymentsProps) => {
  const { id_usuario } = useParams();
  const { client } = useWhitelabel();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = React.useState(false);

  const { dataPaymentBillet, getInstallments, installments } = usePayment();

  async function handleGetInstallments() {
    setIsLoading(true);
    const billets: MappedDebitsToGetInstallments =
      dataPaymentBillet.billets.map((billet) => ({
        type: EnumTypeDebit.BILLET,
        id_bill: billet.id_bill,
      }))
    const carDebits: MappedDebitsToGetInstallments =
      dataPaymentBillet.debits?.filter(debit => !debit?.redirect_to_be_paid).map((debit) => ({
        type: EnumTypeDebit.CAR_DEBIT,
        id_debit: debit.id_debit,
      }))
    const pixDebits: MappedDebitsToGetInstallments = dataPaymentBillet.pix.map(
      ({ id_pix }) => ({
        type: EnumTypeDebit.PIX,
        id_pix,
      })
    );
    const debits: MappedDebitsToGetInstallments = [
      ...billets,
      ...carDebits,
      ...pixDebits,
    ]

    const request = {
      idEstablishment: id_usuario,
      data: { origin: EnumOriginPayment.ONLINE, debits },
    };

    await getInstallments(request, {
      onError(error: any) {
        toast.error(error?.response?.data?.message);
        setIsLoading(false);
      },
    });
    setIsLoading(false);
  }

  const optionsCharge: Record<KeyOptionsCharge, boolean> = useMemo(() => {
    if (!client) {
      return {
        CREDITO: true,
        DEBITO: true,
        PIX: true,
        MULTIPLE: true,
      };
    }

    return {
      CREDITO: !!client.credit_card_enable,
      DEBITO: !!client.debit_card_enable,
      PIX: !!client.pix_enable,
      MULTIPLE: true,
    };
  }, [client]);

  const descriptions = useMemo(() => {
    const MAX_NUMBER_PAYMENT_CREDIT = installments.credit_card?.length;
    const installmentCard =
      installments?.credit_card[MAX_NUMBER_PAYMENT_CREDIT - 1];
    const [installmentDebit] = installments?.debit_card || [];

    return {
      CREDITO: `Em até ${MAX_NUMBER_PAYMENT_CREDIT} vezes de R$ ${formatCurrency(
        installmentCard?.charges?.[0]?.installment_amount || 0
      )}`,
      DEBITO: `Pague à vista por R$ ${formatCurrency(
        installmentDebit?.total_amount || 0
      )}`,
      PIX: `Pague à vista por R$ ${formatCurrency(
        installments?.pix?.total_amount || 0
      )}`,
    };
  }, [installments]);

  const abble2MethodsPayment = useMemo(() => {
    //return !!searchParams.get("public_department")
    return client?.multiple_methods_enable;
  }, [searchParams]);

  useEffect(() => {
    handleGetInstallments();
  }, []);

  return (
    <ContainerFlexCol>
      {optionsCharge.CREDITO && (
        <CardMethodPayment
          data-testid="payment-method-credit"
          title="Cartão de crédito"
          description={descriptions.CREDITO}
          isLoading={isLoading}
          isCard
          onClick={() => {
            setPaymentMethod(EnumFormaPagamento.CREDITO);
          }}
        />
      )}
      {optionsCharge.DEBITO && (
        <CardMethodPayment
          data-testid="payment-method-debit"
          title="Cartão de débito"
          description={descriptions.DEBITO}
          isCard
          isLoading={isLoading}
          onClick={() => {
            setPaymentMethod(EnumFormaPagamento.DEBITO);
          }}
        />
      )}
      {optionsCharge.PIX && (
        <CardMethodPayment
          data-testid="payment-method-pix"
          title="Pix"
          description={descriptions.PIX}
          isPix
          isLoading={isLoading}
          onClick={() => {
            setPaymentMethod(EnumFormaPagamento.PIX);
          }}
        />
      )}
      {optionsCharge.MULTIPLE && (
        <CardMethodPayment
          data-testid="payment-method-multiple"
          title="Múltiplos métodos"
          description="Com até 2 métodos diferentes"
          isCard
          isPix
          isLoading={isLoading}
          onClick={() => {
            setPaymentMethod(EnumFormaPagamento.MULTIPLE);
          }}
          isDisabled={true}
        />
      )}
    </ContainerFlexCol>
  );
};

export default OptionsPayments;
